// Redux
import { AsyncResult, Action, ApiAction } from '../..';
import api from '../../redux-api';

// Types
import { AnnouncementsAdminState, Announcement, AnnouncementPayload } from '../types/announcements';

const ANNOUNCEMENTS_QUERY_LIMIT = 20;
const ANNOUNCEMENT_PARTICIPANTS_QUERY_LIMIT = 10;

const INITIAL_STATE: AnnouncementsAdminState = {
  announcements: [],
  nextAnnouncementsToken: undefined,
  nextParticipantsToken: undefined,
  participants: [],
};

// Actions
const ADD_ANNOUNCEMENT: ApiAction = {
  type: 'connect/announcements/admin/add',
  endpoint: 'notifications',
};

export const DELETE_ANNOUNCEMENT: ApiAction = {
  type: 'connect/announcements/admin/delete',
  endpoint: 'notifications/:id/',
};

const GET_ALL_ANNOUNCEMENTS: ApiAction = {
  type: 'connect/announcements/admin/get-all-announcements',
  endpoint: 'notifications',
};

const GET_ANNOUNCEMENT_PARTICIPANTS: ApiAction = {
  type: 'connect/announcements/admin/get-announcement-participants',
  endpoint: 'notifications/:id/recipients',
};

export default function reducer(state: AnnouncementsAdminState = INITIAL_STATE, action: Action): AnnouncementsAdminState {
  switch (action.type) {
    case `${ADD_ANNOUNCEMENT.type}_SUCCESS`: {
      return {
        ...state,
        announcements: [action.data.data].concat(state.announcements),
      };
    }
    case `${DELETE_ANNOUNCEMENT.type}_SUCCESS`: {
      return {
        ...state,
        announcements: state.announcements.filter(announcement => announcement._id !== action.data.data._id),
        participants: [],
      };
    }
    case `${GET_ALL_ANNOUNCEMENTS.type}_SUCCESS`: {
      const { notifications, next } = action.data.data;

      return {
        ...state,
        announcements: action.params.next ? state.announcements.concat(notifications) : notifications,
        nextAnnouncementsToken: next,
      };
    }
    case `${GET_ALL_ANNOUNCEMENTS.type}_ERROR`: {
      return {
        ...state,
        nextAnnouncementsToken: undefined,
      };
    }
    case `${GET_ANNOUNCEMENT_PARTICIPANTS.type}_SUCCESS`: {
      const { recipients, next } = action.data.data;

      return {
        ...state,
        participants: action.params.next ? state.participants.concat(recipients) : recipients,
        nextParticipantsToken: next,
      };
    }
    case `${GET_ANNOUNCEMENT_PARTICIPANTS.type}_ERROR`: {
      return {
        ...state,
        nextParticipantsToken: undefined,
      };
    }
    default:
      return state;
  }
}

export const addAnnouncement =
  (values: AnnouncementPayload): AsyncResult<Announcement> =>
  () =>
    api.post(ADD_ANNOUNCEMENT, null, values).then((res: { data: any }) => res.data);

export const deleteAnnouncement =
  (announcementId: string): AsyncResult<void> =>
  () =>
    api.delete(DELETE_ANNOUNCEMENT, { id: announcementId });

export const getAnnouncements =
  (query?: string, nextToken?: string): AsyncResult<Announcement[]> =>
  () => {
    type GetAnnouncementsParams = {
      query?: string;
      next?: string;
      limit?: number;
    };

    const params: GetAnnouncementsParams = {
      limit: ANNOUNCEMENTS_QUERY_LIMIT,
    };

    if (query) {
      params.query = query;
    }

    if (nextToken) {
      params.next = nextToken;
    }

    return api.get(GET_ALL_ANNOUNCEMENTS, params).then((res: { data: any }) => res.data);
  };

export const getAnnouncementParticipants =
  (announcementId: string, query?: string, nextToken?: string): AsyncResult<Announcement[]> =>
  () => {
    type GetAnnouncementParticipantsParams = {
      id: string;
      query?: string;
      next?: string;
      limit?: number;
    };

    const params: GetAnnouncementParticipantsParams = {
      id: announcementId,
      limit: ANNOUNCEMENT_PARTICIPANTS_QUERY_LIMIT,
    };

    if (query) {
      params.query = query;
    }

    if (nextToken) {
      params.next = nextToken;
    }

    return api.get(GET_ANNOUNCEMENT_PARTICIPANTS, params).then((res: { data: any }) => res.data);
  };
